import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { LocationService } from '../location/service/location.service';
import { LocationTypeEnum } from '../location/model/location-type.enum';
import { LocationSearchRequest } from '../location/model/location-search-request.model';
import { MatchType } from '../column-filter/column-filter-text-with-match-options-component';
import { JhiEventManager } from '../core-utils';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map, tap } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Location } from '../location/model/location';
import { SubscriptionsManager } from '../util/subscriptions-manager';

@Component({
    selector: 'jhi-locations-select',
    templateUrl: './locations-select.component.html',
    styles: [
        `.divider {
            border-top: 1px solid #ccc;
        }

        div:focus {
            outline: none;
        }
        `
    ]
})
export class LocationsSelectComponent implements OnInit, OnChanges, OnDestroy {

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (this.hasFocus && !this.preventBlur) {
            // Check if the click was outside the container
            // if it does, blur the component
            if (!this.container.nativeElement.contains(event.target)) {
                this.hasFocus = false;
                this.blur.emit();
            }
        }
    }

    @ViewChild('container') container: ElementRef;
    @ViewChild('select') ngSelect: NgSelectComponent;

    @Input() showFilterOptions?: boolean;
    @Input() value: number;
    @Input() cropName?: string;
    @Input() disabled = false;
    @Input() showFavoritesCheckbox = true;
    @Input() showCountryField = false;
    @Input() storageLocationsOnly = false;
    @Input() isBreedingAndCountryLocationsOnly = false;
    @Input() helpLink: string;
    @Input() selectWidth = '100%';
    @Input() clearable = true;
    @Output() valueChange = new EventEmitter<number>();
    @Input() showDivider = false;
    @Output() onLocationChanged = new EventEmitter<Location>();
    @Output() blur = new EventEmitter<void>();
    @Output() keyDown = new EventEmitter<KeyboardEvent>();
    @Input()  appendTo: string = '';
    private preventBlur = false;
    private hasFocus = false;

    // If selectBoxOnly is true, the component will only display the select box with all locations options.
    hideFilterOptions: boolean;
    private loadingSubject = new BehaviorSubject<boolean>(false);
    isLoading = this.loadingSubject.asObservable();

    eventSubscriber: Subscription;

    pageIterator = 0;
    pageSize = 300;
    searchSubject = new Subject<string>();
    locationsOptions: any;
    locationSelected: string;
    useFavoriteLocations = true;
    locationsFilteredItemsCount = 0;
    items: any[];
    locationsMap: Map<number, Location> = new Map<number, Location>();
    countryNameMap: Map<number, string> = new Map<number, string>();
    previousCropName: string;
    countryName: string;
    storageLocationType = [1500];

    subscriptionManager = new SubscriptionsManager();

    constructor(private locationService: LocationService,
                private eventManager: JhiEventManager) {
        this.eventSubscriber = this.eventManager.subscribe('resetLocationToDefault', (event) => {
            this.setLocationToDefault();
        });
    }

    ngOnDestroy(): void {
        this.locationsMap.clear();
        this.countryNameMap.clear();
        this.eventManager.destroy(this.eventSubscriber);
        this.searchSubject.complete();
        this.searchSubject.unsubscribe();
        this.items = [];
        this.locationsOptions = [];
        this.locationSelected = null;
        this.countryName = null;
        this.value = null;
        this.previousCropName = null;
        this.loadingSubject.complete();
        this.subscriptionManager.unsubscribeAll();

    }

    ngOnInit(): void {
        this.useFavoriteLocations = this.showFilterOptions;
        // The locations are retrieved only when the dropdown is opened, so we have to manually set the initial selected item on first load.
        // Get the location method and add it to the initial data.
        if (this.value) {
            this.locationSelected = String(this.value);
            this.locationService.getLocationById(this.locationSelected).toPromise().then((location) => {
                this.items = [{
                    id: String(location.id),
                    label: location.abbreviation ? location.name + ' - (' + location.abbreviation + ')' : location.name,
                    additional: location.countryName
                }];
                this.countryName = location.countryName;
                this.locationsMap.set(location.id, location);
            });
        } else if (!this.cropName) {
            this.setLocationToDefault();
        }

        this.subscriptionManager.add = this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => this.setLoading(true)),
            // your existing search logic
            finalize(() => this.setLoading(false))
        ).subscribe((searchTerm) => {
            this.search(searchTerm);
            this.setLoading(false);
        }, (error) => {
            this.setLoading(false);
        }
        );
    }

    ngOnChanges(changes): void {
        if (this.cropName && this.cropName !== this.previousCropName) {
            this.pageIterator = 0;
            this.items = [];
            this.countryName = null;
            this.locationSelected = null;
            this.value = null;
            this.previousCropName = this.cropName;
            this.fetchMore(null);
        }
    }

    setLocationToDefault(): void {
        if (this.storageLocationsOnly) {
            // Set the selected location to the default location
            this.locationService.getDefaultStorageLocation().toPromise().then((location) => {
                if (location) {
                    this.items = [{
                        id: String(location.id),
                        label: location.abbreviation ? location.name + ' - (' + location.abbreviation + ')' : location.name,
                        additional: location.countryName
                    }];
                    this.value = location.id;
                    this.locationSelected = String(this.value);
                    this.valueChange.emit(Number(this.locationSelected));
                    this.locationsMap.set(location.id, location);
                }
            });
        } else {
            // Set the selected location to the default location
            this.locationService.getDefaultBreedingLocation().toPromise().then((location) => {
                if (location) {
                    this.items = [{
                        id: String(location.id),
                        label: location.abbreviation ? location.name + ' - (' + location.abbreviation + ')' : location.name,
                        additional: location.countryName
                    }];
                    this.value = location.id;
                    this.locationSelected = String(this.value);
                    this.valueChange.emit(Number(this.locationSelected));
                    this.locationsMap.set(location.id, location);
                }
            });
        }
    }

    onSearch(searchTerm: string) {
        this.setLoading(true);
        this.searchSubject.next(searchTerm);
    }

    search(searchTerm: string): void {
        this.setLoading(true);
        this.pageIterator = 0;
        this.items = [];
        try {
            this.subscriptionManager.add = this.getLocations(this.pageIterator, searchTerm).pipe(finalize(() => this.setLoading(false))).subscribe((res) => {
                const locations = res.body;
                this.items = this.items.concat(locations.map((location) => {
                    return {
                        id: String(location.id),
                        label: location.abbreviation ? location.name + ' - (' + location.abbreviation + ')' : location.name,
                        additional: location.countryName
                    };
                }));
            });
        } catch (error) {
            this.setLoading(false);
        }
    }

    private getLocations(page: number, searchTerm = '') {
        const locationSearchRequest: LocationSearchRequest = new LocationSearchRequest();
        locationSearchRequest.locationTypeIds = (this.isBreedingAndCountryLocationsOnly) ? [LocationTypeEnum.BREEDING_LOCATION, LocationTypeEnum.COUNTRY] : [];
        locationSearchRequest.locationNameFilter = {
            type: MatchType.STARTSWITH,
            value: searchTerm
        };

        const pagination = {
            page,
            size: this.pageSize
        };
        if (this.storageLocationsOnly) {
            locationSearchRequest.locationTypeIds = this.storageLocationType;
        }

        const useFaveLocations = this.cropName ? false : this.useFavoriteLocations;

        return this.locationService.searchLocations(locationSearchRequest, useFaveLocations, pagination, this.cropName).pipe(map((res) => {
            this.locationsFilteredItemsCount = Number(res.headers.get('X-Total-Count'));
            const locations = res.body;

            this.countryNameMap = new Map();
            locations.forEach((loc) => {
                this.countryNameMap.set(loc.id, loc.countryName);
                this.locationsMap.set(loc.id, loc);
            });
            return res;
        }));
    }

    fetchMore(searchTerm: string): void {
        this.setLoading(true);
        if (this.canLoadMore()) {
            this.pageIterator = this.pageIterator + 1;
            this.subscriptionManager.add = this.getLocations(this.pageIterator, searchTerm).pipe(finalize(() => this.setLoading(false))).subscribe(
                (res) => {
                    const locations = res.body;
                    this.items = this.items.concat(locations.map((location) => {
                        this.locationsMap.set(location.id, location);
                        return {
                            id: String(location.id),
                            label: location.abbreviation ? location.name + ' - (' + location.abbreviation + ')' : location.name,
                            additional: location.countryName
                        };
                    }));
                }, (error) => {
                    this.setLoading(false);
                });
        } else {
            this.setLoading(false);
        }
    }

    private canLoadMore(): boolean {
        return (this.pageIterator * this.pageSize) < this.locationsFilteredItemsCount;
    }

    onValueChanged($event): void {
        this.pageIterator = 0;
        this.countryName = this.countryNameMap.get(Number($event));
        this.valueChange.emit(Number($event));
        this.onLocationChanged.emit(this.locationsMap.get(Number($event)));
    }

    onBlur() {
        if (this.blur && !this.preventBlur) {
            this.blur.emit();
        }

    }

    onKeyDown(event: KeyboardEvent) {
        if (this.keyDown) {
            this.keyDown.emit(event);
        }

    }

    // Focus this component instead of just the ng-select
    focus(): void {
        setTimeout(() => {
            // this.ngSelect.focus();
            this.container.nativeElement.focus();
        });
    }

    open(): void {
        setTimeout(() => {
            this.ngSelect.open();
        });
    }

    onMouseDown(event: MouseEvent): void {
        this.preventBlur = true;
        this.hasFocus = true;
        // Reset the flag after the current event cycle
        setTimeout(() => {
            this.preventBlur = false;
        }, 0);
    }

    onContainerBlur(event: FocusEvent): void {
        if (!this.preventBlur) {
            // Only emit blur if we're not clicking inside the component
            if (!this.container.nativeElement.contains(event.relatedTarget)) {
                this.hasFocus = false;
                this.blur.emit();
            }
        }
    }

    private setLoading(loading: boolean) {
        this.loadingSubject.next(loading);
    }

}
